import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';

import { FileParameter, FileType } from '@lmt/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { QueryFactory } from '@lmt/shared-types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import {
  defaultExcludedFileTypes,
  getDocumentExtension,
  getDocumentTitle,
  isInvalidExtension,
} from './common';

import styles from './ImportDocument.module.scss';
import dialogStyles from '@lmt/unicorn-shared/app/components/common/Dialog/Dialog.module.scss';
import TrashIcon from '@lmt/unicorn-shared/app/icons/trash.svg?react';
import UploadFileIcon from '@lmt/unicorn-shared/app/icons/upload-file.svg?react';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { ConfirmOptions } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal.types';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import DocumentIcon from '@lmt/unicorn-shared/app/icons/default-document.svg?react';
import { useEventCallback } from '@mui/material';
type ImportDocumentProps = FormComponentProps & {
  blockId: number;
};

const MAX_SIZE = 104857600;

export const ImportDocument: React.FC<ImportDocumentProps> = (props) => {
  const { getRootProps, getInputProps, open, acceptedFiles } = useDropzone({
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
  });

  const { formType, formId, tryCompleteForm, blockId } = props;

  const queryClient = useQueryClient();
  const modals = useModal();
  const i18next = useScopedTranslation('patients.documents.import');
  const [documents, setDocuments] = useState<File[]>([]);
  const [comment, setComment] = useState<string>();
  const [isSaving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    updateDocuments();
  }, [acceptedFiles]);

  const updateDocuments = useEventCallback(() => {
    let x = documents;
    for (const file of acceptedFiles) {
      if (file.path && x.map((document) => document.path).includes(file.path)) {
        x = x.map((document) =>
          document.path === file.path ? (file as File) : document,
        );
      } else x.push(file as File);
    }
    setDocuments([...x]);
  });

  const removeDocument = useEventCallback((removedDocument: File) => {
    setDocuments(documents.filter((document) => document !== removedDocument));
  });

  const files = useMemo(
    () =>
      documents.map((file) => (
        <div
          key={file.path}
          className={styles.documentItem}
          data-test-id="document-row"
        >
          <div className={styles.fileName} data-test-id="document-file-name">
            {file.name}
          </div>
          <div className={styles.options}>
            {file.size > MAX_SIZE ? (
              <div
                className={styles.fileErrorText}
                data-test-id="document-import-error-message"
              >
                {i18next.t('file_size_limit')}
              </div>
            ) : isInvalidExtension(file.name) ? (
              <div
                className={styles.fileErrorText}
                data-test-id="document-import-error-message"
              >
                {i18next.t('invalid_extension')}
              </div>
            ) : null}
            <IconButton
              color="destroy"
              iconComponent={<TrashIcon className={styles.deleteIcon} />}
              onClick={() => removeDocument(file)}
              testId="document-import-remove-button"
            />
          </div>
        </div>
      )),
    [documents.length],
  );

  const onCancel = useEventCallback(async () => {
    await tryCompleteForm(formType, formId, async () => undefined);
  });

  const onSave = useEventCallback(async () => {
    setSaving(true);
    const dataBlock = await QueryFactory.DataBlockClient.getDataBlock(blockId);
    const patientId = dataBlock.patientId ?? null;
    const existingDocuments = await QueryFactory.FileClient.searchFiles(
      null,
      blockId,
      undefined,
      undefined,
      undefined,
      undefined,
      defaultExcludedFileTypes,
    );

    function getNewDocumentName(
      existingNames: string[],
      defaultName: string,
      version: number,
    ): string {
      const name = getDocumentTitle(defaultName);
      const ext = getDocumentExtension(defaultName);
      const newDocumentName = `${name} (${version})${ext}`;
      if (existingNames.includes(newDocumentName)) {
        return getNewDocumentName(existingNames, defaultName, version + 1);
      } else {
        return newDocumentName;
      }
    }

    await tryCompleteForm(formType, formId, async () => {
      let isSaveAsNewVersion = false;
      let isSaveAsNewFile = false;
      let isCancel = false;
      for (const document of documents) {
        try {
          const existingDocument = existingDocuments.find(
            (existingDocument) => document.name === existingDocument.name,
          );
          const documentName = document.name;
          const file: FileParameter = {
            fileName: documentName,
            data: document,
          };

          if (existingDocument) {
            if (isCancel) {
              continue;
            }

            const saveAsNewVersion = async () => {
              await QueryFactory.FileClient.updateFile(
                existingDocument.id,
                file,
              );

              await queryClient.invalidateQueries({
                queryKey: QueryFactory.FileQuery.getVersionHistoryQueryKey(
                  existingDocument.groupId,
                ),
              });
            };

            const saveAsNewFile = async () => {
              file.fileName = getNewDocumentName(
                existingDocuments.map(
                  (existingDocument) => existingDocument.name,
                ),
                documentName,
                1,
              );

              await QueryFactory.FileClient.create(
                FileType.External,
                comment,
                blockId,
                patientId,
                null,
                null,
                null,
                file,
              );
            };

            if (isSaveAsNewVersion) {
              await saveAsNewVersion();
            } else if (isSaveAsNewFile) {
              await saveAsNewFile();
            } else {
              const result = await showReplaceFileModal(modals, {
                title: i18next.t('replace_confirmation_title'),
                text: i18next.t('replace_confirmation_text'),
                fileName: existingDocument.name,
              });
              if (result === null) return;

              if (result.isApplyToAll) {
                if (result.isSaveAsNewVersion) {
                  isSaveAsNewVersion = true;
                } else if (result.isSaveAsNewFile) {
                  isSaveAsNewFile = true;
                } else {
                  isCancel = true;
                }
              }
              if (result.isSaveAsNewVersion) {
                await saveAsNewVersion();
              } else if (result.isSaveAsNewFile) {
                await saveAsNewFile();
              }
            }
          } else {
            await QueryFactory.FileClient.create(
              FileType.External,
              comment,
              blockId,
              patientId,
              null,
              null,
              null,
              file,
            );
          }
        } catch (err) {
          await modals.showError({ text: parseError(err) });
        } finally {
          setSaving(false);
        }
      }
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
          dataBlockId: blockId,
        }),
      });
      if (patientId) {
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.searchFilesQueryKey({ patientId }),
        });
      }
    });
  });

  const isCommentError = (comment ?? '').length > 1024;
  const isSizeLimitError = documents.some((file) => file.size > MAX_SIZE);
  const isInvalidExtensionError = documents.some((file) =>
    isInvalidExtension(file.name),
  );

  return (
    <div className={styles.container} data-test-id="importDocumentPopup">
      <div
        {...(getRootProps({
          className: styles.dropzone,
        }) as any)}
      >
        <input {...(getInputProps() as any)} />
        <UploadFileIcon className={styles.uploadFileIcon} />
        <div className={styles.text}>{i18next.t('import_text')}</div>
        <Button
          color={ButtonColor.Primary}
          title={i18next.t('upload_button')}
          onClick={open}
          className={styles.uploadButton}
        />
      </div>
      {files.length > 0 && (
        <>
          <div
            className={styles.documentsListContainer}
            data-test-id="documents-list-container"
          >
            <div className={styles.documentsTitle}>Documents</div>
            <div className={styles.documentsList}> {files} </div>
          </div>
          <Field className={styles.comment} title={i18next.t('add_comment')}>
            <Input
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              errorText={
                isCommentError ? i18next.t('comment_error_text') : undefined
              }
            />
          </Field>
        </>
      )}
      <div className={styles.buttonContainer}>
        <Button
          className={styles.button}
          onClick={onCancel}
          color={ButtonColor.Primary}
          title={i18next.t('cancel_button')}
        />

        <Button
          disabled={
            !files.length ||
            isSaving ||
            isCommentError ||
            isSizeLimitError ||
            isInvalidExtensionError
          }
          title={i18next.t('save_button')}
          className={styles.button}
          onClick={onSave}
        />
      </div>
    </div>
  );

  async function showReplaceFileModal(
    modals: ReturnType<typeof useModal>,
    options: ConfirmOptions & {
      fileName: string;
      title: string;
    },
  ): Promise<ReplaceFileResult | null> {
    const result = await modals.showCustom<ReplaceFileResult>({
      ...options,
      Controls: (props) => (
        <>
          <CheckBox
            name={'isApplyToAll'}
            title={i18next.t('replace_file_apply_to_all')}
            checked={props.value?.isApplyToAll}
            onChange={() =>
              props.setValue({
                ...(props.value as any),
                isApplyToAll: !props.value?.isApplyToAll,
              })
            }
            className={dialogStyles.checkbox}
          />
          <div className={dialogStyles.buttonContainer}>
            <Button
              color={ButtonColor.Primary}
              title={i18next.t('replace_file_save_as_new_file')}
              onClick={() => {
                props.onClose({
                  isSaveAsNewFile: true,
                  isApplyToAll: props.value?.isApplyToAll ?? false,
                  isSaveAsNewVersion: false,
                });
              }}
            />
            <Button
              color={ButtonColor.Default}
              title={i18next.t('replace_file_save_as_new_version')}
              onClick={() => {
                props.onClose({
                  isSaveAsNewFile: false,
                  isApplyToAll: props.value?.isApplyToAll ?? false,
                  isSaveAsNewVersion: true,
                });
              }}
              testId={'dialog-confirmButton'}
            />
          </div>
        </>
      ),
      Component: (props) => (
        <div>
          <Field
            title={options.text?.toString() || ''}
            titleClassName={styles.modalText}
          >
            <div className={styles.fileContainer}>
              <DocumentIcon className={styles.fileIcon} />
              <div className={styles.fileName}>{options.fileName}</div>
            </div>
          </Field>
        </div>
      ),
    });

    return result;
  }
};
export type ReplaceFileResult = {
  isSaveAsNewVersion: boolean;
  isSaveAsNewFile: boolean;
  isApplyToAll: boolean;
};
